import React, { FC, useState, useCallback, useEffect } from "react";
import { Button, OnboardingAside, OnboardingContent, Input, LoadingSpinner, PageAlert } from "@xapp/components";

import { ClientApp } from "models";

export interface HighValueLeadDescriptionStepProps {
    app: Pick<ClientApp, "appId" | "businessHighValueLeadDescription">;
    /**
     * The generated high value lead description
     */
    generatedDescription?: string;
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Optional aside content
     */
    aside?: React.ReactNode;
    /**
     * Callback when user clicks continue
     */
    onNext?: (props: Pick<ClientApp, "appId" | "businessHighValueLeadDescription">) => void;

    /**
     * Callback when user clicks back
     */
    onBack?: () => void;

    /**
     * Loading state when fetching data
     */
    loading?: boolean;
    /**
     * Saving state when submitting form
     */
    saving?: boolean;
}

export const HighValueLeadDescriptionStep: FC<HighValueLeadDescriptionStepProps> = (props) => {
    const { aside, onNext, onBack, loading, saving, app, generatedDescription } = props;

    const [businessHighValueLeadDescription, setBusinessHighValueLeadDescription] = useState<string>("");

    // Update form field when generatedDescription is provided or app data changes
    useEffect(() => {
        if (generatedDescription) {
            setBusinessHighValueLeadDescription(generatedDescription);
        } else if (app?.businessHighValueLeadDescription) {
            setBusinessHighValueLeadDescription(app.businessHighValueLeadDescription);
        }
    }, [app, generatedDescription]);

    const handleContinue = useCallback(() => {
        onNext?.({
            appId: app.appId,
            businessHighValueLeadDescription,
        });
    }, [businessHighValueLeadDescription, app, onNext]);

    const handleSkip = useCallback(() => {
        onNext?.({
            appId: app.appId,
            businessHighValueLeadDescription: "",
        });
    }, [app, onNext]);

    // Validation: description is optional but if provided should have content
    const hasDescription = businessHighValueLeadDescription.trim().length > 0;

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}

            <OnboardingContent>
                {loading || saving ? (
                    <div
                        style={{
                            padding: "40px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "400px",
                        }}
                    >
                        <LoadingSpinner
                            textStyle={{ fontSize: "1em" }}
                            loadingText={saving ? "Saving your preferences..." : "Loading..."}
                        />
                    </div>
                ) : (
                    <div style={{ padding: "20px", maxWidth: "600px", margin: "0 auto" }}>
                        <h2 style={{ marginBottom: "10px" }}>High Value Lead Description</h2>

                        <PageAlert background={"blue"} style={{ marginBottom: "20px" }}>
                            <p>
                                We've generated a description of high-value leads for your business based on your Google
                                Business Profile. Your AI will use this to identify and prioritize the most valuable
                                opportunities.
                            </p>
                        </PageAlert>

                        <div style={{ marginBottom: "20px" }}>
                            <p style={{ fontSize: "14px", color: "#666", marginBottom: "15px" }}>
                                High value leads are the types of jobs that are most important to your business—those
                                with high margins, require specialized expertise, or tend to lead to more business. Feel
                                free to edit this description to match your preferences.
                            </p>
                            <Input
                                label="High Value Lead Description"
                                value={businessHighValueLeadDescription}
                                onChange={(value) => setBusinessHighValueLeadDescription(String(value))}
                                multiline
                                minRows={5}
                                placeholder="e.g., High value leads include bathroom renovations over $5,000, whole-house re-piping projects, commercial clients, emergency repairs for repeat customers, and any project requiring permits or multiple visits."
                                style={{ width: "100%" }}
                                variation="outlined"
                                helperText="Used to identify and prioritize high-value leads for your business"
                            />
                        </div>

                        {/* Action Buttons */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginTop: "40px",
                            }}
                        >
                            <div>
                                {onBack && <Button label="Back" variation="secondary" onClick={onBack} />}
                            </div>
                            <div style={{ display: "flex", gap: "10px" }}>
                                <Button label="Skip" variation="secondary" onClick={handleSkip} />
                                <Button label="Continue" disabled={!hasDescription} onClick={handleContinue} />
                            </div>
                        </div>
                    </div>
                )}
            </OnboardingContent>
        </>
    );
};
